/**
 * The gateway's spam-check inputs for a plain (no-JS) lead form (services/api-gateway
 * lib/lead-spam.ts). Server-safe: everything here works without client JavaScript.
 *
 * - `blt_hp`: a trap field moved off-screen. People never see or tab to it; form-filling bots
 *   fill it. Neutral name and autocomplete off so browser autofill leaves it alone.
 * - `form_started_at`: when the page was rendered. A submit seconds later is a script. A page
 *   served from cache is older, which only makes the check more lenient.
 * - Turnstile, when the host has a site key: Cloudflare's widget adds `cf-turnstile-response`
 *   to the form, and the gateway verifies it. The script itself is TURNSTILE_SCRIPT, rendered
 *   once per page by BlockRenderer so two contact forms don't load it twice.
 *
 * A form flagged by these is still saved as a lead, under Spam, so nothing real is lost.
 */
export function SpamGuardFields({ turnstileSiteKey }: { turnstileSiteKey?: string | null }) {
  return (
    <>
      <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}>
        <label>
          Leave this field empty
          <input type="text" name="blt_hp" tabIndex={-1} autoComplete="off" defaultValue="" />
        </label>
      </div>
      <input type="hidden" name="form_started_at" defaultValue={String(Date.now())} />
      {/* The form's report on its widget (`blt_ts`). With Turnstile on, a submit with no token is
          spam unless this says the widget couldn't run here; TURNSTILE_ERROR_SCRIPT flips it to
          `error` when Cloudflare's script fails to load or the widget errors. */}
      <input type="hidden" name="blt_ts" defaultValue={turnstileSiteKey ? 'on' : 'off'} />
      {/* Cloudflare's script, loaded once per page by BlockRenderer, renders into this. Hidden
          unless Cloudflare needs the visitor to interact; on an error (e.g. this hostname isn't on
          the widget's allowed list) TURNSTILE_ERROR_CALLBACK hides it rather than leave Cloudflare's
          bare "Troubleshoot" link between the message box and Send, and marks blt_ts `error`. The
          form still submits either way. */}
      {turnstileSiteKey ? (
        <div
          className="cf-turnstile"
          data-sitekey={turnstileSiteKey}
          data-appearance="interaction-only"
          data-retry="never"
          data-error-callback={TURNSTILE_ERROR_CALLBACK}
          data-unsupported-callback={TURNSTILE_ERROR_CALLBACK}
        />
      ) : null}
    </>
  );
}

export const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

/** Global the widgets name in data-error-callback; defined by TURNSTILE_ERROR_SCRIPT. */
export const TURNSTILE_ERROR_CALLBACK = 'bltTurnstileError';

/**
 * Defines TURNSTILE_ERROR_CALLBACK before Cloudflare's script runs. Every widget on a page shares
 * one site key and hostname, so an error in one means all would show it: hide them all, and mark
 * every form's `blt_ts` as `error` so the gateway doesn't read the missing token as a bot.
 * Returning true tells Cloudflare the error is handled. The page's load event also checks that
 * Cloudflare's script arrived at all (an async script blocked or failed to load never fires the
 * callback; load waits for it either way).
 */
export const TURNSTILE_ERROR_SCRIPT =
  `window.${TURNSTILE_ERROR_CALLBACK}=function(){` +
  `document.querySelectorAll('.cf-turnstile').forEach(function(el){el.style.display='none'});` +
  `document.querySelectorAll('input[name="blt_ts"]').forEach(function(el){el.value='error'});` +
  `return true};` +
  `window.addEventListener('load',function(){if(!window.turnstile)window.${TURNSTILE_ERROR_CALLBACK}()});`;
